import React, { useState } from 'react';
import { PublicClientApplication } from '@azure/msal-browser';
import CircularProgress from '@mui/material/CircularProgress';
import { useGoogleLogin } from '@react-oauth/google';
import { ERROR_500 } from 'common/config/api';
import { GOOGLE, MICROSOFT } from 'common/constants/login';
import * as messages from 'common/constants/messages/validations';
import { UNCONTROLLED_ERROR } from 'common/constants/messages/validations';
import { Google as GoogleIcon, Microsoft as MicrosoftIcon } from 'icons';
import { useGetSocialAccessToken } from 'services/hooks/auth';
import { Logger } from 'utilities';
import { GoogleCredentialResponse, LoginProps, SocialLoginPayload, SocialLoginProps } from './SocialLogin.props';
import { ButtonText, SocialButton } from './SocialLogin.styles';

const msalConfig = {
  auth: {
    clientId: `${process.env.REACT_APP_AZURE_ID}`,
  },
};

const SocialLogin = ({ setError, setAlert }: SocialLoginProps) => {
  const getSocialAccessToken = useGetSocialAccessToken();
  const [isLoadingGoogle, setIsLoadingGoogle] = useState(false);
  const [isLoadingMicrosoft, setIsLoadingMicrosoft] = useState(false);
  const msalInstance = new PublicClientApplication(msalConfig);

  const handleSocialLogin = async (payload: SocialLoginPayload): Promise<void> => {
    try {
      const response = await getSocialAccessToken(payload);
      if (response && response.statusCode === ERROR_500) {
        if (payload.provider === GOOGLE) {
          setIsLoadingGoogle(false);
          setAlert(true);
          setError(UNCONTROLLED_ERROR);
        } else if (payload.provider === MICROSOFT) {
          setIsLoadingMicrosoft(false);
          setAlert(true);
          setError(UNCONTROLLED_ERROR);
        }
      }
    } catch (error) {
      throw new Error(error as string);
    }
  };

  const login = async ({ accessToken, provider }: LoginProps): Promise<void> => {
    try {
      if (provider === GOOGLE) {
        const payload: SocialLoginPayload = {
          credential: `Bearer ${accessToken as string}`,
          provider,
        };
        await handleSocialLogin(payload);
      } else if (provider === MICROSOFT) {
        const payload: SocialLoginPayload = {
          id_token: accessToken as { rawIdToken: string; claims: Record<string, unknown> },
          provider,
        };
        await handleSocialLogin(payload);
      }
    } catch (error) {
      setAlert(true);
      setError(messages.UNABLE_TO_LOGIN_TRY_AGAIN);
      Logger.error(messages.UNABLE_TO_LOGIN_TRY_AGAIN, error);
    }
  };

  const handleGoogleLogin = useGoogleLogin({
    onSuccess: ({ access_token: accessToken }: GoogleCredentialResponse) => {
      setIsLoadingGoogle(true);
      login({ accessToken, provider: GOOGLE });
    },
    onError: () => {
      setAlert(true);
      setError(messages.UNABLE_TO_LOGIN_TRY_AGAIN);
    },
    flow: 'implicit',
  });

  const handleMicrosoftLogin = () => {
    msalInstance
      .loginPopup()
      .then(response => {
        setIsLoadingMicrosoft(true);
        const accessToken = {
          rawIdToken: response.idToken,
          claims: response.idTokenClaims as Record<string, unknown>,
        };
        login({ accessToken, provider: MICROSOFT });
      })
      .catch(() => {
        setAlert(true);
        setError(messages.UNABLE_TO_LOGIN_TRY_AGAIN);
      });
  };

  return (
    <>
      <SocialButton
        variant="contained"
        startIcon={!isLoadingGoogle && <GoogleIcon />}
        fullWidth
        onClick={() => handleGoogleLogin()}>
        {isLoadingGoogle ? (
          <CircularProgress size={28} color="inherit" />
        ) : (
          <ButtonText>Continue with Google</ButtonText>
        )}
      </SocialButton>
      <SocialButton
        variant="contained"
        startIcon={!isLoadingMicrosoft && <MicrosoftIcon />}
        fullWidth
        onClick={handleMicrosoftLogin}>
        {isLoadingMicrosoft ? (
          <CircularProgress size={28} color="inherit" />
        ) : (
          <ButtonText>Continue with Microsoft</ButtonText>
        )}
      </SocialButton>
    </>
  );
};

export default SocialLogin;
